import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Input, LoaderCircle, Popup } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { ADMIN_ROLES, useAccess, USER_ROLE } from '@/hooks/access'
import { useSearch } from '@/hooks/searchParams'

import adminQueries from '../queries'
import type { ReportFilters as ReportFiltersType, ReportType } from '../resources'
import adminResources from '../resources'

import { ExportSettings } from './ExportSettings'
import { ReportsContextProvider, useReportsContext } from './index.context'
import { ReportFilters } from './ReportFilters'
import { ReportList } from './ReportsList'
import { CURRENT_YEAR, getDefaultReportDates } from './utils'

export type ReportFilterOptions = {
  selectedReportType: ReportType | undefined
  isGlobal: boolean
  fromDate?: string
  toDate?: string
  year?: string | number
  selectedCustomer?: number
}

const ReportsPage: FC = () => {
  useAccess([...ADMIN_ROLES, USER_ROLE.REPORT_USER])
  const translate = useTranslation()
  const [defaultFromDate, defaultToDate] = getDefaultReportDates()
  const { count, isReportListLoading, setIsReportListLoading, setCustomerSpecificReports } =
    useReportsContext()

  useQuery(adminQueries.adminCustomerSpecificReports(), adminResources.getCustomerSpecificReports, {
    onSuccess: data => {
      setCustomerSpecificReports(data)
    },
  })

  const [isOpen, setIsOpen] = useState(false)
  const { searchFilter, setSearchFilter } = useSearch()

  const [queryFilters, setQueryFilters] = useState<ReportFilterOptions>({
    selectedReportType: undefined,
    isGlobal: false,
    fromDate: defaultFromDate,
    toDate: defaultToDate,
    year: CURRENT_YEAR || '',
    selectedCustomer: undefined,
  })
  const queryClient = useQueryClient()

  useEffect(() => {
    const isReportListLoading = queryClient
      .getQueryCache()
      .getAll()
      .some(
        query =>
          (query.queryKey as string[])[0].endsWith('-reports') &&
          query.state.fetchStatus === 'fetching'
      )
    setIsReportListLoading(isReportListLoading)
  }, [queryClient, queryFilters, setIsReportListLoading])

  return (
    <Container>
      <Container.Header>
        <Translation id="Reports" />
      </Container.Header>

      <ReportFilters
        isReportListLoading={isReportListLoading}
        filters={queryFilters}
        onOpen={() => setIsOpen(true)}
        onSetQueryFilters={(filters: ReportFilterOptions) => {
          if (queryFilters.selectedReportType !== filters.selectedReportType) setSearchFilter('')
          setQueryFilters(filters)
        }}
      />

      {isReportListLoading ? (
        <div className="mt-24 text-center">
          <LoaderCircle />
        </div>
      ) : (
        <>
          {count > 0 && (
            <div className="flex items-center">
              <div>
                <Input
                  id="searchFilter"
                  type="text"
                  value={searchFilter}
                  placeholder={translate('Search')}
                  onChange={e => setSearchFilter(e.target.value)}
                />
              </div>
            </div>
          )}
        </>
      )}

      {isReportTypeSelected(queryFilters) ? <ReportList filters={{ ...queryFilters }} /> : null}

      {isReportTypeSelected(queryFilters) && (
        <Popup
          className="w-12/12 xl:w-5/12"
          ariaLabel={translate('Download Report')}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        >
          <ExportSettings filters={queryFilters} onClose={() => setIsOpen(false)} />
        </Popup>
      )}
    </Container>
  )
}

export function isReportTypeSelected(reports: ReportFilterOptions): reports is ReportFiltersType {
  return Boolean(reports.selectedReportType)
}

export const Reports: FC = () => (
  <ReportsContextProvider>
    <ReportsPage />
  </ReportsContextProvider>
)
